import { useMemo, useState } from "react";
import { format, startOfDay, startOfYear, subDays, subYears } from "date-fns";
import { useTranslation } from "react-i18next";
import { DateRange, Modifiers } from "react-day-picker";
import { Button } from "~/components/button";
import { Icon } from "~/components/icon/icon";
import { StyledCalendar } from "~/components/styled-calendar";
import { IconInput } from "~/components/input/icon-input";
import { Text } from "~/components/text";
import { buttonVariants } from "~/components/ui/button";
import { useAnalytics } from "~/contexts/analytics-context";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { useProduct } from "~/contexts/product-context";
import { useSalesData } from "~/contexts/sales-data-context";
import { cn } from "~/utils";
import {
  clampRangeToReleaseDate,
  currentYearRange,
  DEFAULT_RANGE_WEEKS,
  defaultSalesDateRange,
  formatInputDate,
  formatSalesDateRange,
  isStartClamped,
  isValidSalesDateRange,
  lastYearRange,
  lifetimeRange,
  lifetimeStartDate,
  parseInputDate,
  rangesMatch,
  type SalesDatePresetId,
  type SalesDateRange,
  toIsoDate,
} from "~/shared/sales-data/sales-date-range";
import {
  RangePreset,
  SalesDateRangePickerPresets,
} from "./sales-date-range-picker-presets";
import { StartDateClampHint } from "./start-date-clamp-hint";

type AnalyticsPreset =
  | "last_12_weeks"
  | "year_to_date"
  | "last_year"
  | "lifetime";

type SelectionPhase = "start" | "end";
type EditingField = "start" | "end" | null;

const ANALYTICS_PRESET: Record<SalesDatePresetId, AnalyticsPreset> = {
  "last-12-weeks": "last_12_weeks",
  "current-year": "year_to_date",
  "last-year": "last_year",
  lifetime: "lifetime",
};

const toDraft = ({ start, end }: SalesDateRange): DateRange => ({
  from: start,
  to: end,
});

const compactCalendarClassNames = {
  months: "flex flex-row gap-4",
  month: "space-y-2",
  month_caption: "flex justify-center pt-0 items-center",
  caption_label: "text-sm font-normal",
  button_previous: cn(
    buttonVariants({ variant: "outline" }),
    "w-5 h-5 bg-transparent p-0 opacity-50 hover:opacity-100 absolute left-1 top-3",
  ),
  button_next: cn(
    buttonVariants({ variant: "outline" }),
    "w-5 h-5 bg-transparent p-0 opacity-50 hover:opacity-100 absolute right-1 top-3",
  ),
  weekday:
    "rounded-md font-normal text-[0.7rem] w-8 h-8 flex items-center justify-center p-0 m-0.5 text-secondary-400",
  day: cn(
    buttonVariants({ variant: "ghost" }),
    "w-8 h-8 p-0 m-0.5 text-xs text-center relative flex items-center justify-center font-normal rounded-full",
  ),
};

const findMatchingPreset = (
  presets: RangePreset[],
  range: SalesDateRange,
): RangePreset | undefined =>
  presets.find((preset) => {
    const presetRange = preset.getRange();
    return presetRange ? rangesMatch(range, presetRange) : false;
  });

export const SalesDateRangePicker = () => {
  const { t } = useTranslation(["productDetails"]);
  const { product } = useProduct();
  const { from, to, presetId, setRange, earliestSelectableDate, publishedAt } =
    useSalesData();
  const { gaEvent } = useAnalytics();

  const earliestDate = earliestSelectableDate;
  const today = startOfDay(new Date());

  const presets = useMemo<RangePreset[]>(() => {
    const referenceDay = startOfDay(new Date());
    return [
      {
        id: "last-12-weeks",
        getUnclampedStart: () =>
          subDays(referenceDay, DEFAULT_RANGE_WEEKS * 7 - 1),
        getRange: () => {
          const range = defaultSalesDateRange(
            new Date(),
            earliestSelectableDate,
          );
          return isValidSalesDateRange(range) ? range : undefined;
        },
      },
      {
        id: "current-year",
        getUnclampedStart: () => startOfYear(referenceDay),
        getRange: () => {
          const range = currentYearRange(new Date(), earliestSelectableDate);
          return isValidSalesDateRange(range) ? range : undefined;
        },
      },
      {
        id: "last-year",
        getUnclampedStart: () => startOfYear(subYears(referenceDay, 1)),
        getRange: () => {
          const range = lastYearRange(new Date(), earliestSelectableDate);
          return isValidSalesDateRange(range) ? range : undefined;
        },
      },
      {
        id: "lifetime",
        getUnclampedStart: () => lifetimeStartDate(earliestSelectableDate),
        getRange: () => {
          const range = lifetimeRange(new Date(), earliestSelectableDate);
          return isValidSalesDateRange(range) ? range : undefined;
        },
      },
    ];
  }, [earliestSelectableDate]);

  const fallbackRange = defaultSalesDateRange(
    new Date(),
    earliestSelectableDate,
  );
  const committedRange = from && to ? { start: from, end: to } : undefined;
  const range: SalesDateRange =
    committedRange ??
    (isValidSalesDateRange(fallbackRange)
      ? fallbackRange
      : {
          start: earliestSelectableDate,
          end: earliestSelectableDate,
        });

  const presetLabels: Record<SalesDatePresetId, string> = {
    "last-12-weeks": t("salesData.dateRangePicker.presets.last12Weeks"),
    "current-year": t("salesData.dateRangePicker.presets.currentYear"),
    "last-year": t("salesData.dateRangePicker.presets.lastYear"),
    lifetime: t("salesData.dateRangePicker.presets.lifetime"),
  };

  const committedPreset =
    presetId && presetLabels[presetId as SalesDatePresetId]
      ? presets.find((preset) => preset.id === presetId)
      : findMatchingPreset(presets, range);
  const triggerPresetLabel = committedPreset
    ? presetLabels[committedPreset.id]
    : t("salesData.dateRangePicker.presets.custom");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>(() =>
    toDraft(range),
  );
  const [calendarMonth, setCalendarMonth] = useState<Date>(() => range.start);
  const [startText, setStartText] = useState(() =>
    formatInputDate(range.start),
  );
  const [endText, setEndText] = useState(() => formatInputDate(range.end));
  const [draftPresetId, setDraftPresetId] = useState<
    SalesDatePresetId | undefined
  >(presetId as SalesDatePresetId | undefined);
  const [draftPresetUnclampedStart, setDraftPresetUnclampedStart] = useState<
    Date | undefined
  >();
  const [clampHintAutoShowSignal, setClampHintAutoShowSignal] = useState(0);
  const [selectionPhase, setSelectionPhase] = useState<SelectionPhase>("start");
  const [editingField, setEditingField] = useState<EditingField>(null);

  const syncDraftTexts = (next: DateRange | undefined) => {
    setStartText(next?.from ? formatInputDate(next.from) : "");
    setEndText(next?.to ? formatInputDate(next.to) : "");
    if (next?.from) {
      setCalendarMonth(next.from);
    }
  };

  const applyDraftRange = (
    next: DateRange | undefined,
    options?: {
      presetId?: SalesDatePresetId;
      presetUnclampedStart?: Date;
      selectionPhase?: SelectionPhase;
    },
  ) => {
    setDraft(next);
    syncDraftTexts(next);
    setDraftPresetId(options?.presetId);
    setDraftPresetUnclampedStart(options?.presetUnclampedStart);
    setSelectionPhase(options?.selectionPhase ?? "start");
  };

  const clearDraftPreset = () => {
    setDraftPresetId(undefined);
    setDraftPresetUnclampedStart(undefined);
    setClampHintAutoShowSignal(0);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      applyDraftRange(toDraft(range), {
        presetId: presetId as SalesDatePresetId | undefined,
        selectionPhase: "start",
      });
      setCalendarMonth(range.start);
      setEditingField(null);
      setClampHintAutoShowSignal(0);
    }
    setOpen(nextOpen);
  };

  const handleStartTextChange = (text: string) => {
    setStartText(text);
    clearDraftPreset();
    setSelectionPhase("start");
    const parsed = parseInputDate(text);
    setDraft((current) => ({ from: parsed, to: current?.to }));
    if (parsed) {
      setCalendarMonth(parsed);
    }
  };

  const handleEndTextChange = (text: string) => {
    setEndText(text);
    clearDraftPreset();
    setSelectionPhase("start");
    const parsed = parseInputDate(text);
    setDraft((current) => ({ from: current?.from, to: parsed }));
    if (parsed) {
      setCalendarMonth(parsed);
    }
  };

  const handlePresetSelect = (preset: RangePreset) => {
    const presetRange = preset.getRange();
    if (!presetRange) {
      return;
    }
    const unclampedStart = preset.getUnclampedStart();
    setEditingField(null);
    applyDraftRange(toDraft(presetRange), {
      presetId: preset.id,
      presetUnclampedStart: unclampedStart,
      selectionPhase: "start",
    });
    if (isStartClamped(presetRange, unclampedStart, earliestSelectableDate)) {
      setClampHintAutoShowSignal((current) => current + 1);
    } else {
      setClampHintAutoShowSignal(0);
    }
  };

  const handleDaySelect = (day: Date) => {
    clearDraftPreset();
    const clicked = startOfDay(day);

    if (editingField === "start") {
      applyDraftRange(
        { from: clicked, to: draft?.to },
        { selectionPhase: draft?.to ? "start" : "end" },
      );
      setEditingField(null);
      return;
    }

    if (editingField === "end") {
      applyDraftRange(
        { from: draft?.from, to: clicked },
        { selectionPhase: "start" },
      );
      setEditingField(null);
      return;
    }

    const pickingEnd =
      selectionPhase === "end" && draft?.from != null && draft.to == null;

    if (!pickingEnd) {
      applyDraftRange(
        { from: clicked, to: undefined },
        { selectionPhase: "end" },
      );
      return;
    }

    const fromDate = draft.from!;
    if (clicked >= fromDate) {
      applyDraftRange(
        { from: fromDate, to: clicked },
        { selectionPhase: "start" },
      );
      return;
    }

    applyDraftRange(
      { from: clicked, to: undefined },
      { selectionPhase: "end" },
    );
  };

  const handleSelect = (
    _selected: DateRange | undefined,
    triggerDate: Date,
    modifiers: Modifiers,
  ) => {
    if (modifiers.disabled) {
      return;
    }
    handleDaySelect(triggerDate);
  };

  const handleApply = () => {
    if (!draft?.from || !draft?.to || draft.from > draft.to) {
      return;
    }
    const cappedEnd = draft.to > today ? today : draft.to;
    const { start, end } = clampRangeToReleaseDate(
      { start: draft.from, end: cappedEnd },
      earliestSelectableDate,
    );
    if (!isValidSalesDateRange({ start, end })) {
      return;
    }

    const matchedPreset = draftPresetId
      ? presets.find((preset) => preset.id === draftPresetId)
      : findMatchingPreset(presets, { start, end });

    setRange({
      from: start,
      to: end,
      presetId: matchedPreset?.id,
    });

    gaEvent({
      type: "sales_date_range_selected",
      payload: {
        preset: matchedPreset ? ANALYTICS_PRESET[matchedPreset.id] : "custom",
        start: toIsoDate(start),
        end: toIsoDate(end),
        compare_previous: false,
        view: "single",
      },
    });

    setOpen(false);
  };

  const summary = (() => {
    if (draft?.from && draft?.to) {
      return formatSalesDateRange({ start: draft.from, end: draft.to });
    }
    if (draft?.from) {
      return formatInputDate(draft.from);
    }
    return "";
  })();

  const activePresetId =
    draftPresetId ??
    (draft?.from && draft?.to
      ? findMatchingPreset(presets, { start: draft.from, end: draft.to })?.id
      : undefined);

  const startClamped =
    draft?.from != null &&
    draft?.to != null &&
    isStartClamped(
      { start: draft.from, end: draft.to },
      draftPresetUnclampedStart,
      earliestSelectableDate,
    );

  const startClampMessage = publishedAt
    ? t("salesData.dateRangePicker.startDateClamped", {
        title: product?.title ?? t("salesData.dateRangePicker.thisTitle"),
        date: format(publishedAt, "d MMMM yyyy"),
      })
    : t("salesData.dateRangePicker.startDateClampedNoDate");

  const lastYearPresetRange = lastYearRange(today, earliestSelectableDate);
  const lastYearDisabledReason =
    product?.publishedAt && !isValidSalesDateRange(lastYearPresetRange)
      ? t("salesData.dateRangePicker.lastYearDisabled", {
          title: product.title ?? t("salesData.dateRangePicker.thisTitle"),
          date: format(product.publishedAt.toDate(), "d MMMM yyyy"),
        })
      : undefined;

  const calendarSelection = useMemo((): DateRange | undefined => {
    if (!draft?.from) {
      return undefined;
    }
    if (!draft.to) {
      return { from: draft.from, to: draft.from };
    }
    return { from: draft.from, to: draft.to };
  }, [draft]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="tertiary"
          icon="calendar"
          iconPosition="left"
          testId="sales-date-range-trigger"
        >
          <Text as="span" variant="caption" className="uppercase">
            {triggerPresetLabel}
          </Text>
          <Text as="span" variant="body">
            {formatSalesDateRange(range)}
          </Text>
          <Icon
            name="chevron-down"
            size="small"
            iconClassName={`text-secondary-400 transition-transform duration-200 ${
              open ? "rotate-180" : ""
            }`}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        avoidCollisions={false}
        className="flex max-h-(--radix-popover-content-available-height) w-auto flex-col overflow-y-auto p-2.5"
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-2">
            <label
              className="flex flex-col gap-1"
              htmlFor="sales-date-range-start"
            >
              <span className="flex items-center gap-1">
                <Text as="span" variant="caption">
                  {t("salesData.dateRangePicker.startDate")}
                </Text>
                <StartDateClampHint
                  visible={startClamped}
                  autoShowSignal={clampHintAutoShowSignal}
                  message={startClampMessage}
                />
              </span>
              <IconInput
                id="sales-date-range-start"
                iconName="calendar-days"
                aria-label={t("salesData.dateRangePicker.startDate")}
                placeholder="DD.MM.YYYY"
                className="[&>span]:px-2"
                inputClassName="h-8 text-sm"
                value={startText}
                onFocus={() => setEditingField("start")}
                onChange={(e) => handleStartTextChange(e.target.value)}
              />
            </label>
            <label
              className="flex flex-col gap-1"
              htmlFor="sales-date-range-end"
            >
              <Text as="span" variant="caption">
                {t("salesData.dateRangePicker.endDate")}
              </Text>
              <IconInput
                id="sales-date-range-end"
                iconName="calendar-days"
                aria-label={t("salesData.dateRangePicker.endDate")}
                placeholder="DD.MM.YYYY"
                className="[&>span]:px-2"
                inputClassName="h-8 text-sm"
                value={endText}
                onFocus={() => setEditingField("end")}
                onChange={(e) => handleEndTextChange(e.target.value)}
              />
            </label>
          </div>
          <SalesDateRangePickerPresets
            presets={presets}
            presetLabels={presetLabels}
            activePresetId={activePresetId}
            lastYearDisabledReason={lastYearDisabledReason}
            onPresetSelect={handlePresetSelect}
          />
          <StyledCalendar
            mode="range"
            numberOfMonths={2}
            showOutsideDays={false}
            selected={calendarSelection}
            onSelect={handleSelect}
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            disabled={{ before: earliestDate, after: today }}
            className="p-0"
            classNames={compactCalendarClassNames}
            autoFocus
          />
          <div className="sticky bottom-0 -mx-2.5 -mb-2.5 flex items-center justify-between gap-3 border-t bg-popover px-2.5 pb-2.5 pt-2">
            <Text
              as="span"
              variant="caption"
              data-testid="sales-date-range-summary"
            >
              {summary}
            </Text>
            <div className="flex gap-2">
              <Button
                variant="tertiary"
                size="small"
                label={t("salesData.dateRangePicker.cancel")}
                onClick={() => setOpen(false)}
                testId="sales-date-range-cancel"
              />
              <Button
                variant="primary"
                size="small"
                label={t("salesData.dateRangePicker.apply")}
                disabled={!draft?.from || !draft?.to || draft.from > draft.to}
                onClick={handleApply}
                testId="sales-date-range-apply"
              />
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
